import type { ReactNode } from "react";

import { type AvatarSize } from "@lytra/frontend-common/components/v2/ui/avatar";
import { PersonAvatar } from "@/components/PersonAvatar";
import RelativeTime from "@/components/RelativeTime";
import { cn } from "@/lib/utils";

export interface ThreadCardProps {
  /** The `data-message-id` the deep link and the scroll helpers look for. */
  messageId: string;
  /** Draws the deep-link ring around the card. */
  ringed: boolean;
  /** The author shown with an avatar; null shows neither (a deleted note). */
  name: string | null;
  /** The author's user id, for their picture or colour. */
  personId?: string | null;
  avatarSize: Extract<AvatarSize, "xs" | "sm">;
  /** A caption after the name, truncated before the name is ("hat an Kollegen gesendet", an address). */
  aside?: string | null;
  /** What follows the caption, before the time (the target of a request). */
  afterAside?: ReactNode;
  time: string | null | undefined;
  /** What follows the time: a badge pushed to the right, a menu. */
  trailing?: ReactNode;
  children: ReactNode;
}

/**
 * The frame of one message in a note or send thread: a header with the author, the time and
 * what the row needs on the right, then the body. The ring marks a deep-linked message.
 */
export function ThreadCard({
  messageId,
  ringed,
  name,
  personId,
  avatarSize,
  aside,
  afterAside,
  time,
  trailing,
  children,
}: ThreadCardProps) {
  return (
    <div
      data-message-id={messageId}
      className={cn(
        "flex min-w-0 flex-col gap-3 -mx-2 -my-1 rounded-md px-2 py-1 transition-shadow duration-700",
        ringed && "ring-2 ring-primary ring-offset-2",
      )}
    >
      {/* Name on the left, time and badge or menu on the right of the first line, as on the mail
          cards. A caption ("hat an … gesendet") and what follows it get a line of their own so
          they cannot crowd the name out. */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-2">
          {name !== null && (
            <>
              <PersonAvatar personId={personId} size={avatarSize} name={name} />
              <span className="min-w-0 truncate text-label text-ink">
                {name}
              </span>
            </>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <RelativeTime
              date={time}
              variant="ago"
              className="text-caption text-ink-muted tabular-nums"
            />
            {trailing}
          </div>
        </div>
        {aside || afterAside ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-caption text-ink-muted">
            {aside ? <span className="min-w-0 truncate">{aside}</span> : null}
            {afterAside}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
